import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Clock, Sparkles, Upload, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import ServiceModal from "@/components/services/ServiceModal";
import ServiceImportModal from "@/components/services/ServiceImportModal";
import BulkPhotoUploadModal from "@/components/services/BulkPhotoUploadModal";
import { getEffectivePricingDisplay, SERVICE_CATEGORIES, SERVICE_KIND_BADGE_LABELS } from "@/lib/pricingUtils";
import { SERVICES } from "@/constants/testIds";

const SERVICE_PLACEHOLDER = "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function ServicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [products, setProducts] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { toast } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const [servicesRes, profsRes, productsRes, zonesRes] = await Promise.all([
        api.get("/services"),
        api.get("/team/professionals/list"),
        api.get("/inventory/products"),
        api.get("/zones"),
      ]);
      setServices(servicesRes.data);
      setProfessionals(profsRes.data);
      setProducts(productsRes.data);
      setZones(zonesRes.data);
    } catch (err) {
      toast({ title: "Error al cargar servicios", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const professionalName = (id) => professionals.find((p) => p.id === id)?.name;
  const visibleServices = categoryFilter === "all" ? services : services.filter((s) => s.category === categoryFilter);

  const handleSubmit = async (form, existing) => {
    try {
      if (existing) {
        await api.put(`/services/${existing.id}`, form);
        toast({ title: "Servicio actualizado", description: `${form.name} fue actualizado.` });
      } else {
        await api.post("/services", form);
        toast({ title: "Servicio creado", description: `${form.name} fue agregado al catalogo.` });
      }
      setModalOpen(false);
      setEditingService(null);
      loadData();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/services/${deleteTarget.id}`);
      toast({ title: "Servicio eliminado", description: `${deleteTarget.name} fue eliminado del catalogo.` });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Servicios</h1>
          <p className="text-base text-muted-foreground mt-1">Catalogo de tratamientos que ofrece tu clínica.</p>
        </div>
        {isAdmin && (
          // Fix AI (2026-10-23): en el celular los botones pasan a la linea de abajo en vez de salirse de la pantalla
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setBulkPhotoOpen(true)} data-testid={SERVICES.bulkUploadButton}>
              <Images className="w-4 h-4" /> Subir fotos en lote
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={SERVICES.importButton}>
              <Upload className="w-4 h-4" /> Importar servicios
            </Button>
            <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => { setEditingService(null); setModalOpen(true); }} data-testid={SERVICES.addButton}>
              <Plus className="w-4 h-4" /> Nuevo servicio
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-56" data-testid={SERVICES.categoryFilterSelect}><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {SERVICE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-12">Cargando servicios...</p>
      ) : visibleServices.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid={SERVICES.emptyState}>
          <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            {services.length === 0 ? "Aun no cargaste servicios en tu catalogo." : "No hay servicios en esta categoría."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={SERVICES.grid}>
          {visibleServices.map((s) => {
            const pricingDisplay = getEffectivePricingDisplay(s);
            const kindBadge = SERVICE_KIND_BADGE_LABELS[s.service_kind];
            return (
            <div key={s.id} className="bg-white border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid={`${SERVICES.cardPrefix}${s.id}`}>
              <img src={s.photo_url ? getFileUrl(s.photo_url) : SERVICE_PLACEHOLDER} alt={s.name} className="w-full h-40 object-cover" />
              <div className="p-5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {s.category && (
                    <span className="text-[11px] font-medium text-brand bg-brand-light px-2 py-0.5 rounded-full">{s.category}</span>
                  )}
                  {kindBadge && (
                    <span className="text-[11px] font-medium text-white bg-brand px-2 py-0.5 rounded-full" data-testid={`${SERVICES.cardKindBadgePrefix}${s.id}`}>{kindBadge}</span>
                  )}
                </div>
                <h3 className="font-serif text-xl text-foreground mt-1.5">{s.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 whitespace-pre-line">{s.description}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="w-3.5 h-3.5" /> {s.duration_minutes} min</span>
                  <div className="text-right">
                    {pricingDisplay.isPromoActive ? (
                      <>
                        <span className="block text-xs text-muted-foreground line-through" data-testid={`${SERVICES.cardPromoRegular}${s.id}`}>{pricingDisplay.regularText}</span>
                        <span className="font-serif text-lg text-brand">{pricingDisplay.promoText}</span>
                      </>
                    ) : (
                      <span className="font-serif text-lg text-brand">{pricingDisplay.regularText}</span>
                    )}
                  </div>
                </div>
                {pricingDisplay.isPromoActive && pricingDisplay.promoLabel && (
                  <p className="text-[11px] font-medium text-emerald-700 bg-emerald-50 inline-block px-2 py-0.5 rounded-full mt-2">{pricingDisplay.promoLabel}</p>
                )}
                {s.professional_ids?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2 truncate">
                    {s.professional_ids.map(professionalName).filter(Boolean).join(", ")}
                  </p>
                )}
                {isAdmin && (
                  <div className="flex justify-end gap-1 mt-4 border-t border-border pt-3">
                    <Button variant="ghost" size="icon" onClick={() => { setEditingService(s); setModalOpen(true); }} data-testid={`${SERVICES.editButtonPrefix}${s.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(s)} data-testid={`${SERVICES.deleteButtonPrefix}${s.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <ServiceModal open={modalOpen} onOpenChange={setModalOpen} service={editingService} professionals={professionals} products={products} zones={zones} onSubmit={handleSubmit} />

      <ServiceImportModal open={importOpen} onOpenChange={setImportOpen} onImported={loadData} />

      <BulkPhotoUploadModal open={bulkPhotoOpen} onOpenChange={setBulkPhotoOpen} services={services} onDone={loadData} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={SERVICES.deleteCancelButton}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid={SERVICES.deleteConfirmButton}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
